<template>
  <div v-show-slide="!!v$.$error"
    :id="uniqName('errors')"
  >
    <AlertMessage type="danger" slim>
      {{ v$.$errors[0]?.$message }}.
      <span v-if="v$.$errors.some(e => e.$validator === 'filesHaveNoErrors')"
      > Remove all invalid files before proceeding.</span>
    </AlertMessage>
  </div>

  <FileAttachments
    v-bind="{ ...fileAttachmentProps, ...$attrs }"
    :aria-describedby="uniqName('errors')"
    :aria-invalid="v$.$error ? 'true' : 'false'"
    :aria-required="rules.required ? 'true' : null"
    :attachButtonClass="attachButtonClass"
    file-row-class="file-attach-row"
    files-container-class="bg-primary-lighter p-2 small"
    :processNewFile="processFile"
    removeButtonClass="btn btn-icon"
    :class="{ 'is-invalid': v$.$invalid }"
    v-model="model"
  >
    <template #attach-button-label>
      <slot name="attach-button-label"></slot>
    </template>

    <template #file-info="{ file }">
      <LocalScope
        v-if="file.size"
        :define="{ sizeParts: filesize(file.size).split(' ') }"
        v-slot="{ sizeParts }"
      >
        <div class="opacity-75 text-nowrap">
          {{ sizeParts[0] }}
          <span class="file-size-unit">{{ sizeParts[1] }}</span>
        </div>
      </LocalScope>
    </template>

    <template #file-error="{ file }">
      <span class="text-danger">
        <span class="bi bi-exclamation-circle"></span>
        <span class="visually-hidden">Error: </span>
        {{ file.error }}
      </span>
    </template>
    <template #remove-button-content="{ file }">
      <span class="bi bi-x"></span>
      <span class="visually-hidden">Remove {{ file.name }}</span>
    </template>
  </FileAttachments>
</template>

<script>
import { computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import filesize from 'filesize';
import FileAttachments from '@ssi/vue-common/src/components/formControls/FileAttachments';
import AlertMessage from '../AlertMessage';
import { pick } from '../../helpers/util';
import { filesHaveNoErrors, filesMaxTotalSize } from '../../validators';

export default {
  setup(props) {
    const formData = computed(() => ({ modelValue: props.modelValue }));
    return {
      v$: useVuelidate({
        modelValue: {
          ...props.rules,
          $autoDirty: true,
          filesHaveNoErrors,
          filesMaxTotalSize: filesMaxTotalSize(20)
        }
      }, formData)
    };
  },

  inject: ['config'],

  components: { AlertMessage, FileAttachments },

  props: {
    ...FileAttachments.props,

    rules: {
      type: Object,
      default: () => ({})
    },

    modelValue: {
      type: Array,
      required: true
    }
  },

  computed: {
    allowedFileTypes: ({ config }) =>
      Array.from(new Set(config.allowedFileTypes.map(([, t]) => t))),

    fileAttachmentProps: ({ $props }) => pick($props, Object.keys(FileAttachments.props)),

    model: {
      get() { return this.modelValue; },
      set(value) { this.$emit('update:modelValue', value); }
    }
  },

  methods: {
    filesize: filesize.partial({ round: 1, standard: 'jedec' }),

    async processFile(file) {
      if (!this.allowedFileTypes.includes(file.type)) {
        throw new Error('File type not allowed.');
      }

      await this.processNewFile(file);

      const encoded = await new Promise((resolve, reject) => {
        try {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(file);
        } catch (e) {
          reject(e);
          this.$emit('processing-failed');
        }
      });

      this.$emit('processing-complete');

      return {
        name: file.name,
        size: file.size,
        contentType: file.type,
        contents: encoded,
        // Create a URL so the temporary file can be downloaded if desired
        url: window.URL.createObjectURL(file)
      };
    },

    validate() { return this.v$.$validate(); }
  }
};
</script>

<style lang="scss" scoped>
@import "../../styles/variables";

:deep() {
  .file-attach-row {
    a {
      text-decoration: none;
    }

    &:hover {
      background-color: $primary-light;

      a {
        color: $secondary-dark;
      }
    }
  }

  // Make file sizes and units line up
  .file-size-unit {
    display: inline-block;
    width: 1.5em;
  }
}
</style>
